import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import axios from 'axios';
import { elasticLogValues } from '../../../utils/Api';

const Path2Elastic = () => {
  const [elasticData1, setElasticData1] = useState({}); // For indices[1]
  const [elasticData0, setElasticData0] = useState({}); // For indices[0]

  useEffect(() => {
    const fetchElasticData = async () => {
      try {
        const response = await axios.get(elasticLogValues);
        
        if (response.data.indices && Array.isArray(response.data.indices)) {
          // Find indices by their index names
          const indices = response.data.indices;
          
          // Find confluent_raw index and set to elasticData1
          const rawIndex = indices.find(index => index.index === 'confluent_raw');
          if (rawIndex) {
            setElasticData1(rawIndex);
          }
          
          // Find confluent_filtered index and set to elasticData0
          const filteredIndex = indices.find(index => index.index === 'confluent_filtered');
          if (filteredIndex) {
            setElasticData0(filteredIndex);
          }
          
        }
      } catch (error) {
        console.error('Error fetching Elastic API data for Path2:', error);
      }
    };

    // Call the API immediately when component mounts
    fetchElasticData();

    // Set up interval to call API every 10 seconds
    const interval = setInterval(fetchElasticData, 10000);

    // Cleanup interval when component unmounts
    return () => clearInterval(interval);
  }, []);

  // Dynamic color functions for status and health
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'open': return '#4caf50'; // Green for open
      case 'closed': return '#f44336'; // Red for closed
      default: return '#ab47bc'; // Purple for loading/unknown
    }
  };

  const getHealthColor = (health) => {
    switch(health?.toLowerCase()) {
      case 'green': return '#4caf50'; // Green for healthy
      case 'yellow': return '#ff9800'; // Orange for warning
      case 'red': return '#f44336'; // Red for critical
      default: return '#ffa726'; // Orange for loading/unknown
    }
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'open': return '🟢';
      case 'closed': return '🔴';
      default: return '⚪';
    }
  };

  const getHealthIcon = (health) => {
    switch(health?.toLowerCase()) {
      case 'green': return '💚';
      case 'yellow': return '💛';
      case 'red': return '❤️';
      default: return '⚪';
    }
  };

  // metrics1 - from indices[1]
  const metrics1 = [
    { 
      label: 'Storage Size', 
      value: elasticData1?.storeSize || 'Loading...', 
      color: '#00b4a0',
      icon: '💾',
      description: 'Total storage size used by the Elastic index of raw data.'
    },
    { 
      label: 'No. of Events', 
      value: elasticData1?.docsCount || 'Loading...', 
      color: '#1e90ff',
      icon: '📊',
      description: 'Total number of events stored in the Elastic index of raw data.'
    },
    { 
      label: 'Status', 
      value: elasticData1?.status || 'Loading...', 
      color: getStatusColor(elasticData1?.status),
      icon: getStatusIcon(elasticData1?.status),
      description: 'Current status of the Elastic index of raw data.',
      isStatus: true
    },
    { 
      label: 'Health', 
      value: elasticData1?.health || 'Loading...', 
      color: getHealthColor(elasticData1?.health),
      icon: getHealthIcon(elasticData1?.health),
      description: 'Current health of the Elastic index of raw data.',
      isHealth: true
    }
  ];

  // metrics2 - from indices[0]
  const metrics2 = [
    { 
      label: 'Storage Size', 
      value: elasticData0?.storeSize || 'Loading...', 
      color: '#00b4a0',
      icon: '💾',
      description: 'Total storage size used by the Elastic index of filtered data.'
    },
    { 
      label: 'No. of Events', 
      value: elasticData0?.docsCount || 'Loading...', 
      color: '#1e90ff',
      icon: '📊',
      description: 'Total number of events stored in the Elastic index of filtered data.'
    },
    { 
      label: 'Status', 
      value: elasticData0?.status || 'Loading...', 
      color: getStatusColor(elasticData0?.status),
      icon: getStatusIcon(elasticData0?.status),
      isStatus: true,
      description: 'Current status of the Elastic index of filtered data.'
    },
    { 
      label: 'Health', 
      value: elasticData0?.health || 'Loading...', 
      color: getHealthColor(elasticData0?.health),
      icon: getHealthIcon(elasticData0?.health),
      isHealth: true,
      description: 'Current health of the Elastic index of filtered data.'
    }
  ];
  return (
    <>
        <Grid item xs={12} md={5}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#60a5fa' }}>
            Legacy Path Metrics Elastic 
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 1.5, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
            {metrics1.map((m, i) => (
              <Grid item xs={6} key={m.label + '_1'}>
                <Card sx={{ 
                  background: m.isStatus || m.isHealth ? 
                    `linear-gradient(135deg, rgba(10,34,64,0.9) 0%, ${m.color}20 100%)` : 
                    'rgba(10,34,64,0.85)', 
                  borderRadius: 3, 
                  boxShadow: 3, 
                  height: 100, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: m.isStatus || m.isHealth ? `2px solid ${m.color}40` : 'none',                  
                }}
                title={m.description}
                >
                  <CardContent sx={{ textAlign: 'center', width: '100%', p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: m.color, fontWeight: 700, mb: 0.5 }}>
                      {m.icon && <span style={{ marginRight: '4px' }}>{m.icon}</span>}
                      {m.label}
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 900, 
                        mt: 0.5, 
                        color: m.isStatus || m.isHealth ? m.color : 'white',
                        textShadow: m.isStatus || m.isHealth ? `0 0 10px ${m.color}50` : 'none',
                        textTransform: m.isStatus || m.isHealth ? 'uppercase' : 'none',
                        letterSpacing: m.isStatus || m.isHealth ? '1px' : 'normal'
                      }}
                    >
                      {m.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#60a5fa' }}>
            Filtered Path Metrics Elastic 
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 1.5, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
            {metrics2.map((m, i) => (
              <Grid item xs={6} key={m.label + '_0'}>
                <Card sx={{ 
                  background: m.isStatus || m.isHealth ? 
                    `linear-gradient(135deg, rgba(10,34,64,0.9) 0%, ${m.color}20 100%)` : 
                    'rgba(10,34,64,0.85)', 
                  borderRadius: 3, 
                  boxShadow: 3, 
                  height: 100, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: m.isStatus || m.isHealth ? `2px solid ${m.color}40` : 'none'
                }}
                title={m.description}
                >
                  <CardContent sx={{ textAlign: 'center', width: '100%', p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: m.color, fontWeight: 700, mb: 0.5 }}>
                      {m.icon && <span style={{ marginRight: '4px' }}>{m.icon}</span>}
                      {m.label}
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 900, 
                        mt: 0.5, 
                        color: m.isStatus || m.isHealth ? m.color : 'white',
                        textShadow: m.isStatus || m.isHealth ? `0 0 10px ${m.color}50` : 'none',
                        textTransform: m.isStatus || m.isHealth ? 'uppercase' : 'none',
                        letterSpacing: m.isStatus || m.isHealth ? '1px' : 'normal'
                      }}
                    >
                      {m.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
        </Grid>
    </>
  )
}

export default Path2Elastic